// @ts-strict-ignore
import * as React from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Legend,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { dayjs } from '@/extendedDayjs';
import { DatetimeFormat } from '../../timeHelpers';
import { ChartColors, ChartStatCategoryPalettes } from '../styles/themeMode';
import { StatisticType, StatisticUnit } from '../../types';
import { displayStatValue } from '../../helpers';
import { runwayColors } from '../styles/globalStyles';

export const RECHARTS_DEFAULT_SERIES_COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#8dd1e1', '#a4de6c'];
export const RECHARTS_TOOLTIP_OFFSET = 24;

function getStableSeriesColor(category: string, fallbackColors: string[]) {
    const hash = category.split('').reduce((accumulator, character) => accumulator + character.charCodeAt(0), 0);
    const palette = fallbackColors.length ? fallbackColors : RECHARTS_DEFAULT_SERIES_COLORS;

    return palette[hash % palette.length];
}

interface GraphPoint {
    x: string | number;
    y: number;
    label: string;
    category: string;
    tooltipLabel?: string;
    tooltipGroupLabel?: string;
    tooltipUsers?: Array<{ label: string; value: number }>;
}

interface AreaGraphProps {
    data: GraphPoint[];
    colors: string[];
    hasTimeAxis: boolean;
    hasUserScopedData?: boolean;
    hideXAxis?: boolean;
    benchmarkValue?: number;
    chartColors: ChartColors;
    chartPalettes: ChartStatCategoryPalettes;
    type: StatisticType;
    unit?: StatisticUnit;
    config?: any;
}

interface StackedBarGraphDatum {
    timeBucket: string;
    type: string;
    value: number;
    total?: number;
}

interface StackedBarGraphProps {
    data: StackedBarGraphDatum[];
    type: StatisticType;
    unit?: StatisticUnit;
    chartColors: ChartColors;
    colors?: string[];
    showLegend?: boolean;
    hideYAxis?: boolean;
    appendPadding?: number | number[];
    legend?: boolean | { [key: string]: any };
    yAxis?: boolean | { [key: string]: any };
    tooltip?: boolean | { [key: string]: any };
}

interface HorizontalBarGraphDatum {
    approval: string;
    duration: number;
}

interface HorizontalBarGraphProps {
    data: HorizontalBarGraphDatum[];
    chartColors: ChartColors;
    color: string;
    formatValue: (value: number) => string;
    height: number;
    appendPadding?: number | number[];
    legend?: boolean | { [key: string]: any };
    meta?: { [key: string]: any };
    tooltip?: boolean | { [key: string]: any };
    xAxis?: boolean | { [key: string]: any };
    yAxis?: boolean | { [key: string]: any };
}

function formatLegacyDurationValue(type: StatisticType, value: number) {
    const oneDay = 86400;

    if (value === 0) {
        return '0d';
    }

    if (value <= oneDay / 2) {
        return '< 1d';
    }

    return displayStatValue(type, value);
}

function formatXAxisValue(value: string | number, hasTimeAxis: boolean, includeYear = false) {
    if (hasTimeAxis && typeof value === 'number') {
        return dayjs(value).format(includeYear ? DatetimeFormat.ShortMonthDateYear : DatetimeFormat.ShortMonthDate);
    }

    return String(value);
}

function buildAreaRows(points: GraphPoint[] = []) {
    const rowsByX = new Map<string, Record<string, any>>();
    const categories: string[] = [];

    points.forEach((point) => {
        const category = point.category || 'value';
        const key = String(point.x);

        if (!categories.includes(category)) {
            categories.push(category);
        }

        if (!rowsByX.has(key)) {
            rowsByX.set(key, { x: point.x });
        }

        const row = rowsByX.get(key)!;
        row[category] = point.y;
        row[`rawValue__${category}`] = point.y;
        row[`tooltipValue__${category}`] = point.y;
        row[`label__${category}`] = point.label;
        row[`tooltipLabel__${category}`] = point.tooltipLabel;
        row[`tooltipGroupLabel__${category}`] = point.tooltipGroupLabel;
        row[`tooltipUsers__${category}`] = point.tooltipUsers;
    });

    const rows = Array.from(rowsByX.values()).sort((a, b) => {
        if (typeof a.x === 'number' && typeof b.x === 'number') {
            return a.x - b.x;
        }
        return String(a.x).localeCompare(String(b.x));
    });

    return { rows, categories };
}

function interpolateMissingAreaRows(rows: Record<string, any>[], categories: string[]) {
    const interpolatedRows = rows.map((row) => ({ ...row }));

    categories.forEach((category) => {
        let previousKnownIndex: number | null = null;

        for (let currentIndex = 0; currentIndex < interpolatedRows.length; currentIndex += 1) {
            if (typeof interpolatedRows[currentIndex][category] !== 'number') {
                continue;
            }

            if (previousKnownIndex !== null && currentIndex - previousKnownIndex > 1) {
                const previousRow = interpolatedRows[previousKnownIndex];
                const currentRow = interpolatedRows[currentIndex];
                const previousX = typeof previousRow.x === 'number' ? previousRow.x : previousKnownIndex;
                const currentX = typeof currentRow.x === 'number' ? currentRow.x : currentIndex;
                const previousY = previousRow[category];
                const currentY = currentRow[category];
                const xRange = currentX - previousX;

                for (let gapIndex = previousKnownIndex + 1; gapIndex < currentIndex; gapIndex += 1) {
                    if (typeof interpolatedRows[gapIndex][category] === 'number') {
                        continue;
                    }

                    const gapX =
                        typeof interpolatedRows[gapIndex].x === 'number' ? interpolatedRows[gapIndex].x : gapIndex;
                    const ratio = xRange === 0 ? 0 : (gapX - previousX) / xRange;
                    const nearestRow = ratio <= 0.5 ? previousRow : currentRow;

                    interpolatedRows[gapIndex][category] = previousY + (currentY - previousY) * ratio;
                    interpolatedRows[gapIndex][`tooltipValue__${category}`] =
                        nearestRow[`rawValue__${category}`] ?? nearestRow[category];
                    interpolatedRows[gapIndex][`tooltipLabel__${category}`] =
                        interpolatedRows[gapIndex][`tooltipLabel__${category}`] ??
                        previousRow[`tooltipLabel__${category}`] ??
                        currentRow[`tooltipLabel__${category}`];
                    interpolatedRows[gapIndex][`label__${category}`] =
                        interpolatedRows[gapIndex][`label__${category}`] ??
                        previousRow[`label__${category}`] ??
                        currentRow[`label__${category}`];
                    interpolatedRows[gapIndex][`tooltipUsers__${category}`] =
                        interpolatedRows[gapIndex][`tooltipUsers__${category}`] ??
                        previousRow[`tooltipUsers__${category}`] ??
                        currentRow[`tooltipUsers__${category}`];
                }
            }

            previousKnownIndex = currentIndex;
        }
    });

    return interpolatedRows;
}

function buildStackedRows(data: StackedBarGraphDatum[]) {
    const rowsByBucket = new Map<string, Record<string, any>>();
    const categories: string[] = [];

    data.forEach((datum) => {
        if (!categories.includes(datum.type)) {
            categories.push(datum.type);
        }

        if (!rowsByBucket.has(datum.timeBucket)) {
            rowsByBucket.set(datum.timeBucket, {
                timeBucket: datum.timeBucket,
                total: datum.total ?? 0,
            });
        }

        const row = rowsByBucket.get(datum.timeBucket)!;
        row[datum.type] = datum.value;
        row.total = datum.total ?? row.total ?? 0;
    });

    return { rows: Array.from(rowsByBucket.values()), categories };
}

function getNumericValues(values: Array<number | null | undefined>) {
    return values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value));
}

function getYAxisDomain(values: Array<number | null | undefined>, configuredMin?: number, unit?: StatisticUnit) {
    if (unit === StatisticUnit.Percent) {
        return [0, 100] as [number, number];
    }

    const numericValues = getNumericValues(values);
    if (!numericValues.length) {
        return ['auto', 'auto'] as ['auto', 'auto'];
    }

    const minValue = Math.min(...numericValues);
    const maxValue = Math.max(...numericValues);
    const range = maxValue - minValue;
    const padding = range > 0 ? range * 0.1 : Math.max(Math.abs(maxValue) * 0.1, 1);

    if (minValue >= 0) {
        if (maxValue === 0) {
            return [0, 1] as [number, number];
        }

        return [0, maxValue + padding] as [number, number];
    }

    if (configuredMin != null) {
        return [configuredMin, maxValue + padding] as [number, number];
    }

    return [minValue - padding, maxValue + padding] as [number, number];
}

function resolveHoveredCategoryFromPointer(params: {
    activeLabel?: string | number;
    chartY?: number;
    rows: Record<string, any>[];
    categories: string[];
    yAxisDomain: Array<number | string>;
    chartHeight: number;
    marginTop: number;
    marginBottom: number;
}): string | null {
    const { activeLabel, chartY, rows, categories, yAxisDomain, chartHeight, marginTop, marginBottom } = params;

    if (activeLabel === undefined || chartY === undefined || !categories.length || chartHeight <= 0) {
        return null;
    }

    const row = rows.find((candidate) => candidate.x === activeLabel);
    if (!row) {
        return null;
    }

    const numericValues = collectSeriesValues(rows, categories).filter(
        (value): value is number => typeof value === 'number' && Number.isFinite(value),
    );
    if (!numericValues.length) {
        return null;
    }

    const resolvedMin =
        typeof yAxisDomain[0] === 'number' && Number.isFinite(yAxisDomain[0]) ?
            yAxisDomain[0]
        :   Math.min(...numericValues);
    const resolvedMax =
        typeof yAxisDomain[1] === 'number' && Number.isFinite(yAxisDomain[1]) ?
            yAxisDomain[1]
        :   Math.max(...numericValues);

    if (resolvedMax === resolvedMin) {
        return null;
    }

    const drawableHeight = chartHeight - marginTop - marginBottom;
    if (drawableHeight <= 0) {
        return null;
    }

    let nearestCategory: string | null = null;
    let smallestDistance = Number.POSITIVE_INFINITY;

    categories.forEach((category) => {
        const value = row[category];
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return;
        }

        const normalized = (value - resolvedMin) / (resolvedMax - resolvedMin);
        const yPosition = marginTop + drawableHeight - normalized * drawableHeight;
        const distance = Math.abs(chartY - yPosition);

        if (distance < smallestDistance) {
            smallestDistance = distance;
            nearestCategory = category;
        }
    });

    return nearestCategory;
}

function collectSeriesValues(rows: Record<string, any>[], categories: string[]) {
    return categories.reduce<Array<number | null | undefined>>((accumulator, category) => {
        rows.forEach((row) => {
            accumulator.push(row[category]);
        });
        return accumulator;
    }, []);
}

function buildNumericTicks(maxValue: number, tickInterval?: number) {
    if (!tickInterval || tickInterval <= 0 || !Number.isFinite(maxValue)) {
        return undefined;
    }

    const ticks: number[] = [];
    for (let value = 0; value <= maxValue; value += tickInterval) {
        ticks.push(value);
    }

    if (ticks[ticks.length - 1] !== maxValue) {
        ticks.push(maxValue);
    }

    return ticks;
}

function normalizeAppendPadding(appendPadding?: number | number[]) {
    if (typeof appendPadding === 'number') {
        return {
            top: appendPadding,
            right: appendPadding,
            bottom: appendPadding,
            left: appendPadding,
        };
    }

    if (Array.isArray(appendPadding)) {
        const [top = 12, right = 20, bottom = 20, left = 20] = appendPadding;
        return { top, right, bottom, left };
    }

    return { top: 12, right: 20, bottom: 20, left: 20 };
}

function isConfigObject(value: unknown): value is { [key: string]: any } {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

interface CustomTooltipProps {
    active?: boolean;
    payload?: any[];
    label?: string | number;
    chartColors: ChartColors;
    formatter: (value: number, name: string, item?: any) => [string, string];
    labelFormatter?: (label: string | number) => string;
    footer?: (payload: any[]) => React.ReactNode;
    sortPayload?: (payload: any[]) => any[];
    summary?: (payload: any[]) => React.ReactNode;
    itemsRenderer?: (payload: any[]) => React.ReactNode;
    benchmarkValue?: number;
    benchmarkLabel?: string;
}

interface CustomLegendProps {
    categories: string[];
    colors: string[];
    activeCategory: string | null;
    onCategoryClick: (category: string) => void;
}

export const CustomTooltipContent = ({
    active,
    payload,
    label,
    chartColors,
    formatter,
    labelFormatter,
    footer,
    sortPayload,
    summary,
    itemsRenderer,
    benchmarkValue,
    benchmarkLabel,
}: CustomTooltipProps) => {
    if (!active || !payload?.length) {
        return null;
    }

    const sortedPayload = sortPayload ? sortPayload(payload) : payload;
    if (!sortedPayload?.length) {
        return null;
    }
    const formattedLabel = labelFormatter ? labelFormatter(label ?? '') : String(label ?? '');

    return (
        <div
            style={{
                backgroundColor: chartColors.tooltipBg,
                border: 'none',
                padding: 12,
                color: chartColors.tooltipText,
                position: 'relative',
                zIndex: 10000,
                pointerEvents: 'auto',
                userSelect: 'text',
                WebkitUserSelect: 'text',
            }}
        >
            {formattedLabel ?
                <div
                    style={{
                        color: chartColors.tooltipText,
                        fontSize: 12,
                        marginBottom: 8,
                    }}
                >
                    {formattedLabel}
                </div>
            :   null}
            {summary ? summary(sortedPayload) : null}
            {itemsRenderer ?
                itemsRenderer(sortedPayload)
            :   sortedPayload.map((item) => {
                    const [formattedValue, formattedName] = formatter(Number(item.value), item.name, item);
                    const shouldShowMarker = !summary;

                    return (
                        <div
                            key={item.dataKey ?? item.name}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                color: chartColors.tooltipText,
                                fontSize: 12,
                                marginTop: 4,
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: shouldShowMarker ? 8 : 0,
                                    minWidth: 0,
                                }}
                            >
                                {shouldShowMarker && (
                                    <span
                                        style={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: 999,
                                            backgroundColor: item.color ?? runwayColors.white,
                                            flexShrink: 0,
                                        }}
                                    />
                                )}
                                <span>{formattedName}</span>
                            </div>
                            <span>{formattedValue}</span>
                        </div>
                    );
                })
            }
            {typeof benchmarkValue === 'number' && benchmarkLabel && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        color: chartColors.tooltipText,
                        fontSize: 12,
                        marginTop: 4,
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            minWidth: 0,
                        }}
                    >
                        <span
                            style={{
                                width: 8,
                                height: 2,
                                backgroundColor: chartColors.axisLabel,
                                flexShrink: 0,
                            }}
                        />
                        <span>{benchmarkLabel}</span>
                    </div>
                    <span>{formatter(benchmarkValue, benchmarkLabel)[0]}</span>
                </div>
            )}
            {footer ? footer(sortedPayload) : null}
        </div>
    );
};

export const CustomLegend = ({ categories, colors, activeCategory, onCategoryClick }: CustomLegendProps) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px 16px',
                marginTop: 8,
            }}
        >
            {categories.map((category, index) => {
                const isDimmed = activeCategory !== null && activeCategory !== category;

                return (
                    <button
                        key={category}
                        type="button"
                        onClick={() => onCategoryClick(category)}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            border: 'none',
                            background: 'transparent',
                            padding: 0,
                            margin: 0,
                            cursor: 'pointer',
                            opacity: isDimmed ? 0.45 : 1,
                            color: 'inherit',
                            font: 'inherit',
                        }}
                    >
                        <span
                            style={{
                                width: 10,
                                height: 10,
                                backgroundColor: colors[index] ?? colors[0] ?? '#1677ff',
                                display: 'inline-block',
                            }}
                        />
                        <span>{category}</span>
                    </button>
                );
            })}
        </div>
    );
};

export const OrgOverviewAreaGraph: React.FC<AreaGraphProps> = ({
    data,
    colors,
    hasTimeAxis,
    hasUserScopedData = false,
    hideXAxis = false,
    benchmarkValue,
    chartColors,
    chartPalettes,
    type,
    unit,
    config,
}) => {
    const configData = (config?.data as GraphPoint[] | undefined) ?? data;
    const resolvedHasTimeAxis = config?.meta?.x?.type === 'time' || hasTimeAxis;
    const resolvedHideXAxis = config?.xAxis?.label == null ? true : hideXAxis;
    const resolvedColors =
        Array.isArray(config?.color) ? config.color
        : typeof config?.color === 'string' ? [config.color]
        : colors.length ? colors
        : RECHARTS_DEFAULT_SERIES_COLORS;
    const { rows: baseRows, categories } = React.useMemo(() => buildAreaRows(configData), [configData]);
    const rows = React.useMemo(() => interpolateMissingAreaRows(baseRows, categories), [baseRows, categories]);

    if (!rows.length || !categories.length) {
        return null;
    }

    const lineWidth = config?.line?.style?.lineWidth ?? 4;
    const fill = config?.areaStyle?.fill ?? chartPalettes.areaFill;
    const fillOpacity = config?.areaStyle?.fillOpacity ?? 0;
    const configuredMinX = config?.xAxis?.min;
    const configuredMaxX = config?.xAxis?.max;
    const configuredMinY = config?.yAxis?.min;
    const showTooltipTitle = config?.tooltip?.showTitle !== false;
    const yAxisDomain = getYAxisDomain(
        [...collectSeriesValues(rows, categories), benchmarkValue],
        configuredMinY,
        unit,
    );
    const [hoveredCategory, setHoveredCategory] = React.useState<string | null>(null);
    const containerRef = React.useRef<HTMLDivElement | null>(null);
    const seriesColors = React.useMemo(
        () =>
            categories.reduce<Record<string, string>>((accumulator, category, index) => {
                accumulator[category] =
                    hasUserScopedData ?
                        getStableSeriesColor(category, resolvedColors)
                    :   (resolvedColors[index] ?? resolvedColors[0]);

                return accumulator;
            }, {}),
        [categories, hasUserScopedData, resolvedColors],
    );
    const chartMargin = React.useMemo(
        () => ({
            top: 8,
            right: 8,
            left: 0,
            bottom: resolvedHideXAxis ? 8 : 12,
        }),
        [resolvedHideXAxis],
    );

    return (
        <div ref={containerRef} style={{ width: '100%', height: '100%' }}>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                    data={rows}
                    margin={chartMargin}
                    onMouseMove={(state) => {
                        if (!hasUserScopedData) {
                            return;
                        }

                        setHoveredCategory(
                            resolveHoveredCategoryFromPointer({
                                activeLabel: state?.activeLabel,
                                chartY: state?.activeCoordinate?.y,
                                rows,
                                categories,
                                yAxisDomain,
                                chartHeight: containerRef.current?.clientHeight ?? 0,
                                marginTop: chartMargin.top,
                                marginBottom: chartMargin.bottom,
                            }),
                        );
                    }}
                    onMouseLeave={() => {
                        if (hasUserScopedData) {
                            setHoveredCategory(null);
                        }
                    }}
                >
                    <CartesianGrid stroke={chartColors.gridLine} vertical />
                    <XAxis
                        dataKey="x"
                        type={resolvedHasTimeAxis ? 'number' : 'category'}
                        hide={resolvedHideXAxis}
                        height={resolvedHideXAxis ? 0 : 20}
                        tickLine={false}
                        axisLine={false}
                        interval="preserveStartEnd"
                        tickCount={resolvedHasTimeAxis ? 5 : undefined}
                        tickMargin={6}
                        domain={
                            resolvedHasTimeAxis ? [configuredMinX ?? 'dataMin', configuredMaxX ?? 'dataMax'] : undefined
                        }
                        tickFormatter={(value) => formatXAxisValue(value, resolvedHasTimeAxis)}
                        tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                    />
                    <YAxis hide domain={yAxisDomain} />
                    {typeof benchmarkValue === 'number' && (
                        <ReferenceLine
                            y={benchmarkValue}
                            stroke={chartColors.axisLabel}
                            strokeDasharray="6 6"
                            strokeWidth={2}
                            ifOverflow="extendDomain"
                        />
                    )}
                    <Tooltip
                        wrapperStyle={{ zIndex: 10000 }}
                        offset={RECHARTS_TOOLTIP_OFFSET}
                        allowEscapeViewBox={{ x: true, y: true }}
                        cursor={{ fill: chartColors.tooltipBg, fillOpacity: 0.1 }}
                        content={
                            <CustomTooltipContent
                                chartColors={chartColors}
                                labelFormatter={(value) =>
                                    showTooltipTitle ?
                                        formatXAxisValue(value as string | number, resolvedHasTimeAxis, true)
                                    :   ''
                                }
                                formatter={(value, name, item) => {
                                    const rawValue = item?.payload?.[`rawValue__${name}`];
                                    const tooltipValue = item?.payload?.[`tooltipValue__${name}`];
                                    const sourceLabel = item?.payload?.[`label__${name}`];
                                    const sourceTooltipLabel = item?.payload?.[`tooltipLabel__${name}`];

                                    return [
                                        displayStatValue(type, Number(tooltipValue ?? rawValue ?? value), unit),
                                        sourceTooltipLabel || sourceLabel || name,
                                    ];
                                }}
                                sortPayload={(payload) =>
                                    [...payload]
                                        .filter((item) => {
                                            if (!hasUserScopedData || !hoveredCategory) {
                                                return true;
                                            }

                                            return String(item?.dataKey ?? item?.name ?? '') === hoveredCategory;
                                        })
                                        .sort((left, right) => {
                                            const leftDataKey = String(left?.dataKey ?? left?.name ?? '');
                                            const rightDataKey = String(right?.dataKey ?? right?.name ?? '');
                                            return (
                                                Number(
                                                    right?.payload?.[`tooltipValue__${rightDataKey}`] ??
                                                        right?.payload?.[`rawValue__${rightDataKey}`] ??
                                                        right.value,
                                                ) -
                                                Number(
                                                    left?.payload?.[`tooltipValue__${leftDataKey}`] ??
                                                        left?.payload?.[`rawValue__${leftDataKey}`] ??
                                                        left.value,
                                                )
                                            );
                                        })
                                }
                                summary={
                                    hasUserScopedData ?
                                        (payload) => {
                                            const firstItem = payload[0];
                                            const dataKey = String(firstItem?.dataKey ?? firstItem?.name ?? '');
                                            const groupLabel =
                                                firstItem?.payload?.[`tooltipGroupLabel__${dataKey}`] ??
                                                firstItem?.payload?.[`label__${dataKey}`];
                                            const total = payload.reduce(
                                                (sum, item) =>
                                                    sum +
                                                    Number(
                                                        item?.payload?.[
                                                            `tooltipValue__${String(item?.dataKey ?? item?.name ?? '')}`
                                                        ] ??
                                                            item?.payload?.[
                                                                `rawValue__${String(item?.dataKey ?? item?.name ?? '')}`
                                                            ] ??
                                                            0,
                                                    ),
                                                0,
                                            );

                                            return (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 12,
                                                        color: chartColors.tooltipText,
                                                        fontSize: 12,
                                                        marginBottom: 8,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                width: 8,
                                                                height: 8,
                                                                borderRadius: 999,
                                                                backgroundColor: firstItem?.color ?? runwayColors.white,
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                        <span>{groupLabel}</span>
                                                    </div>
                                                    <span>{displayStatValue(type, total, unit)}</span>
                                                </div>
                                            );
                                        }
                                    :   null
                                }
                                itemsRenderer={
                                    hasUserScopedData ?
                                        (payload) => {
                                            const firstItem = payload[0];
                                            const dataKey = String(firstItem?.dataKey ?? firstItem?.name ?? '');
                                            const tooltipUsers =
                                                firstItem?.payload?.[`tooltipUsers__${dataKey}`] ??
                                                ([] as Array<{ label: string; value: number }>);

                                            return tooltipUsers.map((user) => (
                                                <div
                                                    key={user.label}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: 12,
                                                        color: chartColors.tooltipText,
                                                        fontSize: 12,
                                                        marginTop: 4,
                                                    }}
                                                >
                                                    <span>{user.label}</span>
                                                    <span>{displayStatValue(type, user.value, unit)}</span>
                                                </div>
                                            ));
                                        }
                                    :   null
                                }
                                benchmarkValue={benchmarkValue}
                                benchmarkLabel="Runway average"
                            />
                        }
                    />
                    {categories.map((category, index) => (
                        <Area
                            key={category}
                            type="monotone"
                            dataKey={category}
                            stroke={seriesColors[category] ?? resolvedColors[index] ?? resolvedColors[0]}
                            fill={fill}
                            fillOpacity={fillOpacity}
                            strokeWidth={lineWidth}
                            dot={false}
                            activeDot={{ r: 4 }}
                            isAnimationActive={false}
                            connectNulls
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
};

export const OrgOverviewStackedBarGraph: React.FC<StackedBarGraphProps> = ({
    data,
    type,
    unit,
    chartColors,
    colors = [],
    showLegend = true,
    hideYAxis = false,
    appendPadding,
    legend,
    yAxis,
}) => {
    const { rows, categories } = React.useMemo(() => buildStackedRows(data), [data]);
    const [activeCategory, setActiveCategory] = React.useState<string | null>(null);
    const resolvedYAxisConfig = isConfigObject(yAxis) ? yAxis : undefined;
    const configuredMinY = resolvedYAxisConfig?.min;
    const configuredTickInterval = resolvedYAxisConfig?.tickInterval;
    const resolvedHideYAxis = hideYAxis || resolvedYAxisConfig?.label === null;
    const resolvedShowLegend = typeof legend === 'boolean' ? legend : showLegend;
    const numericValues = React.useMemo(() => collectSeriesValues(rows, categories), [categories, rows]);
    const resolvedPadding = React.useMemo(() => normalizeAppendPadding(appendPadding), [appendPadding]);
    const yAxisDomain = React.useMemo(
        () => getYAxisDomain(numericValues, configuredMinY, unit),
        [numericValues, configuredMinY, unit],
    );
    const resolvedMaxY = React.useMemo(() => {
        const values = getNumericValues(numericValues);
        return values.length ? Math.max(...values) : 0;
    }, [numericValues]);
    const yAxisTicks = React.useMemo(
        () => buildNumericTicks(resolvedMaxY, configuredTickInterval),
        [configuredTickInterval, resolvedMaxY],
    );
    const displayedCategories = React.useMemo(() => [...categories].reverse(), [categories]);
    const resolvedSeriesColors = React.useMemo(
        () => (colors.length ? colors : RECHARTS_DEFAULT_SERIES_COLORS),
        [colors],
    );

    if (!rows.length || !categories.length) {
        return null;
    }

    const handleCategoryClick = (category: string) => {
        setActiveCategory((currentCategory) => (currentCategory === category ? null : category));
    };

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={rows}
                margin={{
                    top: resolvedPadding.top,
                    right: resolvedPadding.right,
                    bottom:
                        Array.isArray(appendPadding) ? resolvedPadding.bottom
                        : resolvedShowLegend ? resolvedPadding.bottom
                        : 0,
                    left: resolvedPadding.left,
                }}
            >
                <CartesianGrid stroke={chartColors.gridLine} vertical={false} />
                <XAxis
                    dataKey="timeBucket"
                    hide
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                />
                <YAxis
                    hide={resolvedHideYAxis}
                    domain={yAxisDomain}
                    tickLine={false}
                    axisLine={false}
                    tickCount={yAxisTicks ? undefined : 5}
                    ticks={yAxisTicks}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                    tickMargin={6}
                    tickFormatter={(value) => formatLegacyDurationValue(type, Number(value))}
                />
                <Tooltip
                    wrapperStyle={{ zIndex: 10000 }}
                    offset={RECHARTS_TOOLTIP_OFFSET}
                    allowEscapeViewBox={{ x: true, y: true }}
                    cursor={{ fill: chartColors.tooltipBg, fillOpacity: 0.1 }}
                    content={
                        <CustomTooltipContent
                            chartColors={chartColors}
                            formatter={(value, name) => [formatLegacyDurationValue(type, Number(value)), name]}
                            labelFormatter={(label) => String(label)}
                            sortPayload={(payload) =>
                                [...payload].sort(
                                    (left, right) =>
                                        displayedCategories.indexOf(String(right.dataKey ?? right.name)) -
                                        displayedCategories.indexOf(String(left.dataKey ?? left.name)),
                                )
                            }
                            footer={(payload) => {
                                const total = payload?.[0]?.payload?.total;
                                if (typeof total !== 'number' || !Number.isFinite(total)) {
                                    return null;
                                }

                                return (
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 12,
                                            color: chartColors.tooltipText,
                                            fontSize: 12,
                                            marginTop: 10,
                                            paddingTop: 6,
                                        }}
                                    >
                                        <span>Total:</span>
                                        <span>{formatLegacyDurationValue(type, total)}</span>
                                    </div>
                                );
                            }}
                        />
                    }
                />
                {resolvedShowLegend && (
                    <Legend
                        verticalAlign="bottom"
                        align="center"
                        content={
                            <CustomLegend
                                categories={displayedCategories}
                                colors={displayedCategories.map(
                                    (category) =>
                                        resolvedSeriesColors[
                                            categories.indexOf(category) % resolvedSeriesColors.length
                                        ],
                                )}
                                activeCategory={activeCategory}
                                onCategoryClick={handleCategoryClick}
                            />
                        }
                    />
                )}
                {displayedCategories.map((category) => {
                    const index = categories.indexOf(category);

                    return (
                        <Bar
                            key={category}
                            dataKey={category}
                            stackId="value"
                            hide={activeCategory !== null && activeCategory !== category}
                            fill={resolvedSeriesColors[index % resolvedSeriesColors.length]}
                            isAnimationActive={false}
                        />
                    );
                })}
            </BarChart>
        </ResponsiveContainer>
    );
};

export const OrgOverviewHorizontalBarGraph: React.FC<HorizontalBarGraphProps> = ({
    data,
    chartColors,
    color,
    formatValue,
    height,
    appendPadding,
    meta,
    xAxis,
    yAxis,
}) => {
    if (!data.length) {
        return null;
    }

    const resolvedPadding = normalizeAppendPadding(appendPadding);
    const resolvedYAxisConfig = isConfigObject(yAxis) ? yAxis : undefined;
    const resolvedMetaDuration = isConfigObject(meta?.duration) ? meta.duration : undefined;
    const resolvedFormatValue =
        typeof resolvedMetaDuration?.formatter === 'function' ? resolvedMetaDuration.formatter : formatValue;
    const xAxisDomain = getYAxisDomain(data.map((datum) => datum.duration));

    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={data}
                layout="vertical"
                margin={{
                    top: resolvedPadding.top,
                    right: resolvedPadding.right,
                    left: resolvedPadding.left,
                    bottom: resolvedPadding.bottom,
                }}
            >
                <CartesianGrid horizontal={false} stroke={chartColors.gridLine} />
                <XAxis
                    type="number"
                    hide={xAxis === false}
                    domain={xAxisDomain}
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                    tickFormatter={resolvedFormatValue}
                />
                <YAxis
                    type="category"
                    dataKey="approval"
                    hide={yAxis === false}
                    tickLine={false}
                    axisLine={false}
                    width={resolvedYAxisConfig?.width ?? 180}
                    tick={{ fill: chartColors.axisLabel, fontSize: 10 }}
                />
                <Tooltip
                    wrapperStyle={{ zIndex: 10000 }}
                    offset={RECHARTS_TOOLTIP_OFFSET}
                    allowEscapeViewBox={{ x: true, y: true }}
                    cursor={{ fill: chartColors.tooltipBg, fillOpacity: 0.1 }}
                    content={
                        <CustomTooltipContent
                            chartColors={chartColors}
                            formatter={(value) => [resolvedFormatValue(Number(value)), 'Time']}
                        />
                    }
                />
                <Bar dataKey="duration" fill={color} isAnimationActive={false} />
            </BarChart>
        </ResponsiveContainer>
    );
};
